import {
  FGCustomPanel,
  FGSelectInput,
  FGTextAreaInput,
  FGTextInput,
  FieldGroup,
  FGListen,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import {
  CharacteristicApi,
  CharacteristicsDto,
  CharacteristicTranslationDto,
  ELab,
} from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import { FGMultiTranslate, SmallFormGenerator } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { CharacteristicsTypeSelector } from "./CharacteristicsTypeSelector";

export interface ICharacteristicsDetailPageProps {
  characteristicId: number;
  editMode: boolean;
}

export const CharacteristicsDetailPage: React.FunctionComponent<ICharacteristicsDetailPageProps> = ({
  characteristicId,
  editMode,
}) => {
  const { t } = useTl();
  const history = useHistory();
  const [isTypeLab, setisTypeLab] = React.useState(false);
  const [isListValueType, setIsListValueType] = React.useState(false);
  const [
    isDysplasiaCharacteristic,
    setIsDysplasiaCharacteristic,
  ] = React.useState(false);
  const api = useApiService(CharacteristicApi);
  const { languages, currentLanguage } = useGlobalData();

  const [characteristicTypes, cloading] = useReferential(
    (a) => a.getCharacteristicTypes(currentLanguage.languageId),
    true,
    []
  );
  const [dysplasiaTypes, dtloading] = useReferential(
    (a) => a.getDysplasiaTypes(false,0),
    true,
    []
  );
  const [healthValueTypes, hvtLoading] = useReferential(
    (a) => a.getHealthValueTypes(isDysplasiaCharacteristic),
    true,
    [isDysplasiaCharacteristic]
  );

  const [labs, lLoading] = useReferential((a) => a.getLabs(), false, []);

  const { data, loading, saveItem, saving, deleteItem, deleting } = useCrudApi(
    {
      getApiFn: (id = characteristicId) =>
        id > 0
          ? api.get(id)
          : new CharacteristicsDto({
              lab: ELab.ZOOLYX,
              characteristicValuesIds: [],
              characteristicTranslations: [
                ...languages.map(
                  (l) =>
                    new CharacteristicTranslationDto({
                      languageId: l.languageId,
                    })
                ),
              ],
            }).toJSON(),
      saveApiFn: (d: CharacteristicsDto) => api.save(d),
      onSavedRoute: (d) =>
        `${ERoutes.characteristics}/${d.characteristicId}/detail/edit`,
      deleteApiFn: (d) => api.delete(d.characteristicId),
      onDeletedRoute: (d) => `${ERoutes.characteristics}`,
    },
    [characteristicId]
  );

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      code: Yup.string()
        .nullable()
        .required(t(ETLCodes.Required))
        .test(
          "is-unique",
          t(ETLCodes.CodeAlreadyExists),
          async function (value) {
            if (!value || characteristicId > 0) return true;
            const res = await api.isCodeUnique(
              characteristicId,
              this.parent.characteristicTypeId,
              value
            );
            return res.value;
          }
        ),
      characteristicTypeId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test(
          "is-unique",
          t(ETLCodes.CodeAlreadyExists),
          async function (value) {
            if (!value || characteristicId > 0) return true;
            const res = await api.isCodeUnique(
              characteristicId,
              value,
              this.parent.code
            );
            return res.value;
          }
        ),
      characteristicTranslations: Yup.array().of(
        Yup.object().shape({
          text: Yup.string().nullable().required(t(ETLCodes.Required)),
        })
      ),
      lab: Yup.string().nullable().required(t(ETLCodes.Required)),
      dysplasiaTypeId: Yup.number().nullable(),
    });
  }, [api, characteristicId, t]);

  return (
    data && (
      <SmallFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        editMode={editMode}
        loading={loading}
        onCancel={() => history.push(ERoutes.characteristics)}
        saving={saving}
        deleting={deleting}
        showDeleteButton={characteristicId > 0}
        onDelete={deleteItem}
        validationSchema={FormSchema}
      >
        <FGListen
          field="characteristicTypeId"
          onChanged={async (value) => {
            setisTypeLab((await api.isTypeLab(value)).value);
            const isDysplasia = await api.isDysplasiaType(value);
            setIsDysplasiaCharacteristic(isDysplasia.value);
          }}
        />
        <FGListen
          field="healthValueTypeId"
          onChanged={async (value) => {
            const result = await api.isHealthValueTypeList(value);
            setIsListValueType(result.value);
          }}
        />
        <FieldGroup>
          <FGSelectInput
            name="characteristicTypeId"
            items={characteristicTypes}
            loading={cloading}
            label={t(ETLCodes.CharacteristicType)}
            readonly={characteristicId > 0}
          />
          <FGTextInput
            name="code"
            label={t(ETLCodes.Code)}
            readonly={characteristicId > 0}
          />
          <FGTextInput
            name="labCode"
            label={t(ETLCodes.LabCode)}
            disabled={!isTypeLab}
          />
          <FGSelectInput
            name="dysplasiaTypeId"
            items={dysplasiaTypes}
            loading={dtloading}
            label={t(ETLCodes.DysplasiaType)}
            disabled={!isDysplasiaCharacteristic}
          />
          <FGMultiTranslate
            label={ETLCodes.Name}
            translationsArrayField="characteristicTranslations"
            translationField="text"
            translationDto={CharacteristicTranslationDto}
          />
          <FGTextAreaInput label={t(ETLCodes.Remark)} name="remark" />
          <FGSelectInput
            name="lab"
            label={t(ETLCodes.Laboratory)}
            items={labs}
            loading={lLoading}
            disabled={!isTypeLab}
          />
          <FGSelectInput
            name="healthValueTypeId"
            items={healthValueTypes}
            loading={hvtLoading}
            label={t(ETLCodes.HealthValueType)}
          />
        </FieldGroup>
        <FGCustomPanel>
          {({ formik }) =>
            formik?.values?.characteristicValuesIds &&
            isListValueType && (
              <CharacteristicsTypeSelector
                linkedIds={formik?.values?.characteristicValuesIds || null}
                onLinkedIdsChanged={(ids) =>
                  formik?.setFieldValue("characteristicValuesIds", ids)
                }
                editMode={editMode}
                loading={loading}
              />
            )
          }
        </FGCustomPanel>
      </SmallFormGenerator>
    )
  );
};
